import React from "react";
import {Navigate, Outlet, useLocation} from "react-router-dom";
import {useAuth} from "../context/AuthContext";
import {useBusinessAccess} from "../hooks/useBusinessAccess";
import {Role, ROLES} from "../lib/roles";

export {ROLES};

function hasAnyRole(userRoles: string[] | undefined, allowed: readonly Role[]) {
    if (!allowed.length) return true;
    const set = new Set(userRoles ?? []);
    return allowed.some((r) => set.has(r));
}

/** Require the user to be logged in */
export function RequireAuth({
                                redirectTo = "/login",
                            }: {
    redirectTo?: string;
}) {
    const {user, isReady} = useAuth();
    const loc = useLocation();

    if (!isReady) return null; // or a spinner
    if (!user) {
        const returnTo = encodeURIComponent(loc.pathname + loc.search);
        return <Navigate to={`${redirectTo}?returnTo=${returnTo}`} replace/>;
    }
    return <Outlet/>;
}

/** Require the user to have at least one of the allowed roles */
export function RequireRoles({
                                 roles,
                                 fallbackTo = "/",
                             }: {
    roles: readonly Role[];
    fallbackTo?: string;
}) {
    const {user, isReady} = useAuth();

    if (!isReady) return null;
    if (!user) return <Navigate to="/login" replace/>;
    if (!hasAnyRole(user.roles, roles)) return <Navigate to={fallbackTo} replace/>;

    return <Outlet/>;
}

/**
 * Require access to the business the user is currently working in: either an admin
 * role there, or the employee role plus one of the listed permissions granted there.
 * Only usable inside the BusinessProvider tree.
 */
export function RequireBusinessPermission({
                                              permissions,
                                              fallbackTo = "/",
                                          }: {
    permissions: readonly string[];
    fallbackTo?: string;
}) {
    const {isReady, canAccess} = useBusinessAccess();

    if (!isReady) return null;
    if (!canAccess(...permissions)) return <Navigate to={fallbackTo} replace/>;

    return <Outlet/>;
}

/** For login/signup/etc: if already logged in, redirect away (e.g. to /profile) */
export function OnlyAnonymous({redirectTo = "/profile"}: { redirectTo?: string }) {
    const {user, isReady} = useAuth();
    if (!isReady) return null;
    if (user) return <Navigate to={redirectTo} replace/>;
    return <Outlet/>;
}